import DeviceDataTable from '@components/_shared/DeviceDataTable/DeviceDataTable';
import { Button } from '@mui/material';
import { DataGridProProps, GridRowSpacingParams } from '@mui/x-data-grid-pro';
import { generateSx } from '@src/components/_shared/sxStyles/Datatable/deviceTableList';
import UIBox from '@src/components/_shared/UIBox';
import { DEVICE_CONFIGS } from '@src/constants/deviceConfigs';
import useDeviceListWidget from '@src/hooks/devices/useDeviceListWidget';
import useExpandedRows from '@src/hooks/useExpandedRows';
import { Device } from '@src/types/Device';
import { DeviceType } from '@src/types/devices/DeviceConfig.types';
import { ReactElement, useCallback, useMemo, useRef } from 'react';
import DeviceDetailPanel from './DeviceDetailPanel';
import { DeviceWidgetProvider } from './DeviceWidgetProvider';

interface DeviceListPageProps {
  currentUnit: Device;
  deviceType: DeviceType;
  isUnitScope?: boolean;
}

const DeviceListPage = ({
  currentUnit,
  deviceType,
  isUnitScope = true,
}: DeviceListPageProps): ReactElement => {
  const { stylePrefix, widgetIds } = DEVICE_CONFIGS[deviceType];
  const sx = generateSx(stylePrefix);
  const collapseAllRef = useRef<(() => void) | null>(null);

  const { widgetConfig, isLoading, isError } = useDeviceListWidget({
    currentUnit,
    widgetId: widgetIds.list,
    isUnitScope,
  });

  const { expandedRowIds, setExpandedRowIds } = useExpandedRows(
    widgetConfig?.devices,
    5,
    deviceType,
  );

  const handleCollapseAllReady = useCallback((collapseAll: () => void) => {
    collapseAllRef.current = collapseAll;
  }, []);

  const handleCollapseAll = useCallback(() => {
    collapseAllRef.current?.();
    setExpandedRowIds([]);
  }, [setExpandedRowIds]);

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridProProps['getDetailPanelContent']>
  >(
    ({ row }) => (
      <DeviceDetailPanel
        row={row}
        unitControllerId={currentUnit.deviceId}
        unitControllerEngineeringId={currentUnit.engineeringId}
      />
    ),
    [currentUnit],
  );

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  const headerText = useMemo(() => {
    const widgetHeader = widgetConfig?.header;
    return isUnitScope
      ? `${currentUnit?.name} ${widgetHeader}`.trim()
      : widgetHeader;
  }, [currentUnit, widgetConfig?.header, isUnitScope]);

  const getRowClassName = useCallback(
    () => `MuiDataGrid-clickable ${stylePrefix}-row`,
    [stylePrefix],
  );

  const getRowSpacing = useCallback(
    (_params: GridRowSpacingParams) => ({ top: 9, bottom: 4 }),
    [],
  );

  return (
    <DeviceWidgetProvider deviceType={deviceType} isUnitScope={isUnitScope}>
      <UIBox
        header={headerText}
        isError={isError}
        headerAction={
          <Button
            variant='outlined'
            size='small'
            onClick={handleCollapseAll}
            sx={{
              color: '#666',
              borderColor: '#666',
              opacity: 0.7,
              fontSize: '0.75rem',
              padding: '2px 8px',
              minWidth: 'auto',
              transition: 'none',
              '&:hover': {
                borderColor: '#444',
                opacity: 1,
              },
            }}
          >
            Collapse All
          </Button>
        }
      >
        <DeviceDataTable
          widgetId={widgetConfig?.internalId}
          sx={sx.wrapper}
          hideFooter
          disableColumnMenu
          devices={widgetConfig?.devices}
          dataPoints={widgetConfig?.dataPoints ?? []}
          pointConfigRefs={widgetConfig?.pointConfigRefs ?? []}
          useDualListView={widgetConfig?.useDualListView ?? false}
          disableRowSelectionOnClick
          getHeaderHeight={() => 0}
          getRowClassName={getRowClassName}
          getDetailPanelHeight={getDetailPanelHeight}
          getDetailPanelContent={getDetailPanelContent}
          density='standard'
          loading={isLoading}
          initialExpandedDetailPanelIds={expandedRowIds}
          additionalClassnames={`${stylePrefix}-data-table`}
          getRowSpacing={getRowSpacing}
          disableVirtualization
          onCollapseAllReady={handleCollapseAllReady}
          onExpandedRowIdsChange={setExpandedRowIds}
        />
      </UIBox>
    </DeviceWidgetProvider>
  );
};

DeviceListPage.displayName = 'DeviceListPage';

export default DeviceListPage;
